import _ from 'lodash';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import { Link } from '@treams/treams-routes';
import { Heading, Jumbo, Tabs } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import OverallScoreMetric from './metrics/engagement/OverallScoreMetric';
import ParticipationRatesMetric from './metrics/engagement/ParticipationRatesMetric';
import EngagementMetric from './metrics/engagement/EngagementMetric';
import AllScoresTable from './metrics/engagement/AllScoresTable';
import EngagementQuestions from './metrics/engagement/EngagementQuestions';
import EngagementNpsAnalytics from 'analytics/v2/EngagementNpsAnalytics';

const Column = styled(Box)`
	flex-shrink: 0;
`;

const Anchor = styled(Link)`
	color: ${(props) => props.theme.colors.primary};
	text-decoration: underline;

	&:hover {
		text-decoration: none;

		&:active {
			color: black;
		}
	}
`;

function DashboardEngagementMetrics(props: {
	data:
		| graphql.WorkspaceEngagementMetricsQuery
		| graphql.EngagementOfficerMetricsQuery
		| graphql.GroupEngagementMetricsQuery;
	filters: graphql.AnalyticsFilters;
}) {
	const { t } = useTranslation();
	const { isEngagementOfficer, isWorkspaceSuperuser } = useViewerRoles();

	const data =
		'workspaceAnalytics' in props.data
			? props.data.workspaceAnalytics
			: 'groupAnalytics' in props.data
			? props.data.groupAnalytics
			: props.data.engagementAnalytics;

	const questions = data.thrivingQuestions.concat(data.decliningQuestions);

	const participation =
		data.engagementGroupsParticipation as graphql.AnalyticsEngagementGroupParticipation[];

	return (
		<Box>
			<Jumbo
				title={t('dashboard.engagement.title')}
				wide
				footer={
					<Tabs.Container wide>
						<Tabs>
							<Tabs.Item
								title={t('dashboard.organization.title')}
								path={
									isEngagementOfficer
										? '/analytics/employee'
										: isWorkspaceSuperuser
										? '/analytics'
										: '/analytics/department'
								}
							/>
							<Tabs.Item
								title={t('dashboard.engagement.title')}
								selected
								path="/analytics/engagement"
							/>
						</Tabs>
					</Tabs.Container>
				}
			/>
			<Box p={[3, 3, 4]}>
				<Flex flexWrap="wrap">
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<OverallScoreMetric
								data={data.engagementScoreOverTime as graphql.AnalyticsEngagementScore}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<ParticipationRatesMetric
								data={
									data.engagementParticipationOverTime as graphql.AnalyticsEngagementParticipation
								}
							/>
						</Box>
					</Column>
				</Flex>
				<Heading mt={5} mb={3}>
					{t('dashboard.engagement.thrivingTopics.title')}
				</Heading>
				<Flex flexWrap="wrap">
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementThrivingSubcategories[0] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementThrivingSubcategories[1] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
				</Flex>
				<Box mt={2}>
					<Anchor to="/analytics/engagement/topics">
						{t('dashboard.engagement.topics.viewAll')}
					</Anchor>
				</Box>
				<Heading mt={5} mb={3}>
					{t('dashboard.engagement.decliningTopics.title')}
				</Heading>
				<Flex flexWrap="wrap">
					<Column width={[1, 1, 1 / 2]}>
						<Box mr={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementDecliningSubcategories[0] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
					<Column width={[1, 1, 1 / 2]}>
						<Box ml={[0, 0, 2]}>
							<EngagementMetric
								data={
									data
										.engagementDecliningSubcategories[1] as graphql.AnalyticsEngagementSubcategoryScoreOverTime
								}
							/>
						</Box>
					</Column>
				</Flex>
				<Box mt={2}>
					<Anchor to="/analytics/engagement/topics">
						{t('dashboard.engagement.topics.viewAll')}
					</Anchor>
				</Box>
				{data.engagementGroupsSubcategoriesScore.length > 0 && (
					<Box mt={5}>
						<AllScoresTable
							scores={
								data.engagementGroupsSubcategoriesScore as graphql.AnalyticsEngagementGroupSubcategoryScore[]
							}
							participation={[
								...participation,
								{
									participation: data.engagementParticipationOverTime.average ?? undefined,
									delta: data.engagementParticipationOverTime.delta ?? undefined,
								},
							]}
						/>
					</Box>
				)}
				{questions.length > 0 && (
					<>
						<Box mt={5}>
							<EngagementQuestions
								questions={
									_.uniqBy(
										questions,
										({ question }) => question.id,
									) as graphql.AnalyticsEngagementQuestionScoreTrend[]
								}
							/>
						</Box>
						<Box mt={2}>
							<Anchor to="/analytics/engagement/questions">
								{t('dashboard.engagement.questions.viewAll')}
							</Anchor>
						</Box>
					</>
				)}

				<Heading mt={5} mb={3}>
					{t('dashboard.engagement.nps.title')}
				</Heading>

				<EngagementNpsAnalytics filters={props.filters} />
			</Box>
		</Box>
	);
}

function WorkspaceEngagement(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useWorkspaceEngagementMetricsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} filters={props.filters} />;
}

function GroupEngagement(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useGroupEngagementMetricsQuery({
		variables: {
			filters: props.filters,
		},
		skip: !props.filters.groupIDs || props.filters.groupIDs.length === 0,
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} filters={props.filters} />;
}

function EngagementOfficerEngagement(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useEngagementOfficerMetricsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <DashboardEngagementMetrics data={query.data} filters={props.filters} />;
}

export default function DashboardEngagementContainer(props: { filters: graphql.AnalyticsFilters }) {
	const userRoles = useViewerRoles();

	if (userRoles.isEngagementOfficer) {
		return <EngagementOfficerEngagement filters={props.filters} />;
	}

	if (userRoles.isWorkspaceSuperuser || userRoles.isWorkspaceSupervisor) {
		return <WorkspaceEngagement filters={props.filters} />;
	}

	return <GroupEngagement filters={props.filters} />;
}
